import { useCallback, useEffect, useMemo, useId } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import debounce from 'lodash.debounce'
import {
  streamTypeahead,
} from 'actions/SearchActions'

interface UseTypeaheadStreamingOptions {
  enabled?: boolean
  debounceMs?: number
  inputThreshold?: number
  types?: Array<string>
  limit?: number
  isLuxPlusMember?: boolean
  rollout?: Array<string>
  temporaryDoNotExtract?: boolean
}

interface UseTypeaheadStreamingReturn {
  streamingData: App.ApiCallState<App.TypeaheadStreamingData> | undefined
  cancelDebounce: () => void
}

export function useTypeaheadStreaming(
  input: string,
  typeaheadId: string,
  options: UseTypeaheadStreamingOptions = {},
): UseTypeaheadStreamingReturn {
  const {
    enabled = true,
    debounceMs = 150,
    inputThreshold = 2,
    types,
    limit,
    isLuxPlusMember,
    rollout,
    temporaryDoNotExtract = true,
  } = options

  const dispatch = useAppDispatch()
  // Generate a unique listener ID for this hook instance to track state listeners.
  // This allows the socket listener system to know when to clean up - when all
  // state listeners for a typeaheadId are removed, socket listeners are cleaned up.
  const listenerId = useId()

  // Get current state for the typeaheadId
  const streamingData = useAppSelector(
    state => state.search.typeaheadStreamingData[typeaheadId],
  )

  // Create debounced version of streamTypeahead action
  const debouncedStreamTypeahead = useMemo(
    () => debounce((currentInput: string) => {
      dispatch(
        streamTypeahead({
          input: currentInput,
          typeaheadId,
          listenerId,
          types,
          limit,
          isLuxPlusMember,
          rollout,
          temporaryDoNotExtract,
        }),
      )
    }, debounceMs),
    [
      debounceMs,
      typeaheadId,
      listenerId,
      types,
      limit,
      isLuxPlusMember,
      rollout,
      temporaryDoNotExtract,
      dispatch,
    ],
  )

  // Stable cancel function that can be called externally
  const cancelDebounce = useCallback(() => {
    debouncedStreamTypeahead.cancel()
  }, [debouncedStreamTypeahead])

  // Effect for input changes
  useEffect(() => {
    if (!enabled || input.length < inputThreshold) {
      debouncedStreamTypeahead.cancel()
      return
    }

    debouncedStreamTypeahead(input)

    return () => {
      debouncedStreamTypeahead.cancel()
    }
  }, [input, enabled, inputThreshold, debouncedStreamTypeahead, typeaheadId, dispatch])

  return {
    streamingData,
    cancelDebounce,
  }
}
